import type { ColumnDef, SortingState } from "@tanstack/react-table";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { clsx } from "clsx";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Banknote,
  CheckCircle,
  CreditCard,
  Download,
  FileSpreadsheet,
  Receipt,
  RotateCcw,
  TrendingUp,
  Users,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { PERMISSIONS } from "@/auth/permissions";
import { Badge, Card, EmptyState, Select, Spinner } from "@/components/common";
import { UsersPagination } from "@/components/admin/UsersPagination";
import { AgentSelect } from "@/components/imports/AgentSelect";
import { PageHeader } from "@/components/layout/PageHeader";
import { useUser } from "@/hooks/useAuth";
import { useHasPermission } from "@/hooks/usePermission";
import {
  useAgentSettlements,
  useAgentSettlementsAggregate,
  useAllSettlements,
  useExportSettlementExcel,
  useMarkSettlementPaid,
  useSettlementsAggregate,
  useUnlockSettlement,
} from "@/hooks/useSettlements";
import { UnlockSettlementModal } from "@/components/settlements/UnlockSettlementModal";
import { SettlementApprovalView } from "./SettlementApprovalView";
import type {
  AgentSettlement,
  Settlement,
  SettlementStatus,
} from "@/types/settlement";

const MONTHS = [
  "Styczeń",
  "Luty",
  "Marzec",
  "Kwiecień",
  "Maj",
  "Czerwiec",
  "Lipiec",
  "Sierpień",
  "Wrzesień",
  "Październik",
  "Listopad",
  "Grudzień",
];

const STATUS_CONFIG: Record<
  SettlementStatus,
  {
    label: string;
    variant: "default" | "info" | "warning" | "success" | "brand";
  }
> = {
  Draft: { label: "Szkic", variant: "default" },
  Calculated: { label: "Obliczone", variant: "info" },
  Approved: { label: "Zatwierdzone", variant: "warning" },
  Paid: { label: "Zapłacone", variant: "success" },
  Exported: { label: "Wyeksportowane", variant: "brand" },
};

const AGENT_STATUS_CONFIG: Record<
  string,
  { label: string; variant: "default" | "warning" | "success" | "brand" }
> = {
  Approved: { label: "Zatwierdzone", variant: "warning" },
  Paid: { label: "Zapłacone", variant: "success" },
  Exported: { label: "Zapłacone", variant: "success" },
};

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("pl-PL", {
    style: "currency",
    currency: "PLN",
  }).format(value);
}

function yearOptions(): { value: string; label: string }[] {
  const currentYear = new Date().getFullYear();
  const options = [];
  for (let y = currentYear; y >= currentYear - 5; y--) {
    options.push({ value: String(y), label: String(y) });
  }
  return options;
}

const MONTH_OPTIONS = [
  { value: "all", label: "Wszystkie miesiące" },
  ...MONTHS.map((name, i) => ({ value: String(i + 1), label: name })),
];

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "all", label: "Wszystkie statusy" },
  { value: "Calculated", label: "Obliczone" },
  { value: "Approved", label: "Zatwierdzone" },
  { value: "Paid", label: "Zapłacone" },
  { value: "Exported", label: "Wyeksportowane" },
];

const AGENT_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "all", label: "Wszystkie statusy" },
  { value: "Approved", label: "Zatwierdzone" },
  { value: "Paid", label: "Zapłacone" },
];

// ============================
// Summary Stats Component
// ============================

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

function StatCard({ icon, label, value }: StatCardProps) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-edge-soft bg-panel px-4 py-3">
      <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-subtle text-dim">
        {icon}
      </div>
      <div className="min-w-0">
        <p className="truncate text-xs text-dim">{label}</p>
        <p className="truncate text-sm font-semibold text-main">{value}</p>
      </div>
    </div>
  );
}

// ============================
// Generic Data Table
// ============================

interface DataTableProps<T extends { agentShare: number }> {
  data: T[];
  columns: ColumnDef<T, unknown>[];
  sorting: SortingState;
  onSortingChange: (s: SortingState) => void;
}

function DataTable<T extends { agentShare: number }>({
  data,
  columns,
  sorting,
  onSortingChange,
}: DataTableProps<T>) {
  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: (updater) => {
      const next = typeof updater === "function" ? updater(sorting) : updater;
      onSortingChange(next);
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="border-b border-edge bg-subtle">
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className={clsx(
                    "px-4 py-3 text-left text-xs font-medium text-dim",
                    header.column.getCanSort() && "cursor-pointer select-none",
                  )}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  <div className="flex items-center gap-1">
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                    {header.column.getCanSort() &&
                      (header.column.getIsSorted() === "asc" ? (
                        <ArrowUp size={14} />
                      ) : header.column.getIsSorted() === "desc" ? (
                        <ArrowDown size={14} />
                      ) : (
                        <ArrowUpDown size={14} className="text-faint" />
                      ))}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody className="divide-y divide-edge-soft">
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id} className="transition-colors hover:bg-subtle">
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className="px-4 py-3 text-sm text-main">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Summary row */}
      <div className="flex items-center justify-between border-t border-edge bg-subtle px-4 py-3">
        <span className="text-sm font-medium text-sub">
          Razem ({data.length} {data.length === 1 ? "rozliczenie" : "rozliczeń"}
          )
        </span>
        <span className="text-sm font-semibold text-main">
          {formatCurrency(
            data.reduce(
              (sum, s) => sum + s.agentShare,
              0,
            ),
          )}
        </span>
      </div>
    </div>
  );
}

// ============================
// Admin columns
// ============================

const adminHelper = createColumnHelper<Settlement>();

function useAdminColumns(
  onApprove: (settlementId: string) => void,
  onUnlock: (settlementId: string) => void,
  isAdmin: boolean,
) {
  const canApprove = useHasPermission(PERMISSIONS.SETTLEMENTS.UPDATE);
  const markPaid = useMarkSettlementPaid();
  const exportExcel = useExportSettlementExcel();

  return useMemo(
    (): ColumnDef<Settlement, unknown>[] => [
      adminHelper.accessor("agentFullName", {
        header: "Agent",
        cell: (info) => <span className="font-medium">{info.getValue()}</span>,
      }) as ColumnDef<Settlement, unknown>,
      adminHelper.accessor("month", {
        header: "Miesiąc",
        cell: (info) => MONTHS[info.getValue() - 1] ?? info.getValue(),
      }) as ColumnDef<Settlement, unknown>,
      adminHelper.accessor("policyCount", {
        header: "Polisy",
      }) as ColumnDef<Settlement, unknown>,
      adminHelper.accessor("totalCommission", {
        header: "Prowizja łączna",
        cell: (info) => formatCurrency(info.getValue()),
      }) as ColumnDef<Settlement, unknown>,
      adminHelper.accessor("agentShare", {
        header: "Prowizja agenta",
        cell: (info) => formatCurrency(info.getValue()),
      }) as ColumnDef<Settlement, unknown>,
      adminHelper.accessor("agentCommissionRate", {
        header: "Stawka",
        cell: (info) => `${(info.getValue() * 100).toFixed(0)}%`,
      }) as ColumnDef<Settlement, unknown>,
      adminHelper.accessor("status", {
        header: "Status",
        cell: (info) => {
          const status = info.getValue();
          const config = STATUS_CONFIG[status];
          const revisionCount = info.row.original.revisionCount ?? 0;
          return (
            <div className="flex items-center gap-1.5">
              <Badge variant={config.variant} dot>
                {config.label}
              </Badge>
              {revisionCount > 0 && (
                <span title="Liczba korekt rozliczenia">
                  <Badge variant="default">Rewizja ×{revisionCount}</Badge>
                </span>
              )}
            </div>
          );
        },
      }) as ColumnDef<Settlement, unknown>,
      adminHelper.display({
        id: "actions",
        header: "",
        cell: ({ row }) => {
          const s = row.original;
          return (
            <div className="flex items-center justify-end gap-1">
              {canApprove && s.status === "Calculated" && (
                <button
                  type="button"
                  onClick={() => {
                    onApprove(s.id);
                  }}
                  className="rounded-lg p-1.5 text-green-600 transition-colors hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20"
                  title="Zatwierdź"
                >
                  <CheckCircle size={16} />
                </button>
              )}
              {canApprove && s.status === "Approved" && (
                <button
                  type="button"
                  onClick={() => {
                    markPaid.mutate(s.id);
                  }}
                  disabled={markPaid.isPending}
                  className="rounded-lg p-1.5 text-blue-600 transition-colors hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
                  title="Oznacz jako zapłacone"
                >
                  <CreditCard size={16} />
                </button>
              )}
              {/* Backend dopuszcza cofnięcie wyłącznie administratorowi (UnlockSettlement.cs),
                  więc rola z samym settlements.update nie może tu dostać przycisku — dostałaby 403. */}
              {canApprove && isAdmin && s.status === "Approved" && (
                <button
                  type="button"
                  onClick={() => {
                    onUnlock(s.id);
                  }}
                  className="rounded-lg p-1.5 text-orange-600 transition-colors hover:bg-orange-50 dark:text-orange-400 dark:hover:bg-orange-900/20"
                  title="Cofnij do edycji"
                >
                  <RotateCcw size={16} />
                </button>
              )}
              {(s.status === "Approved" ||
                s.status === "Paid" ||
                s.status === "Exported") && (
                <button
                  type="button"
                  onClick={() => {
                    exportExcel.mutate(s.id);
                  }}
                  disabled={exportExcel.isPending}
                  className="rounded-lg p-1.5 text-dim transition-colors hover:bg-muted"
                  title="Pobierz Excel"
                >
                  <Download size={16} />
                </button>
              )}
            </div>
          );
        },
      }) as ColumnDef<Settlement, unknown>,
    ],
    [canApprove, isAdmin, onApprove, onUnlock, markPaid, exportExcel],
  );
}

// ============================
// Agent columns
// ============================

const agentHelper = createColumnHelper<AgentSettlement>();

function useAgentColumns(): ColumnDef<AgentSettlement, unknown>[] {
  const exportExcel = useExportSettlementExcel();

  return useMemo(
    (): ColumnDef<AgentSettlement, unknown>[] => [
      agentHelper.accessor("month", {
        header: "Miesiąc",
        cell: (info) => MONTHS[info.getValue() - 1] ?? info.getValue(),
      }) as ColumnDef<AgentSettlement, unknown>,
      agentHelper.accessor("policyCount", {
        header: "Polisy",
      }) as ColumnDef<AgentSettlement, unknown>,
      agentHelper.accessor("agentShare", {
        header: "Kwota wypłaty",
        cell: (info) => formatCurrency(info.getValue()),
      }) as ColumnDef<AgentSettlement, unknown>,
      agentHelper.accessor("status", {
        header: "Status",
        cell: (info) => {
          const status = info.getValue();
          const config = AGENT_STATUS_CONFIG[status] ?? {
            label: status,
            variant: "default" as const,
          };
          return (
            <Badge variant={config.variant} dot>
              {config.label}
            </Badge>
          );
        },
      }) as ColumnDef<AgentSettlement, unknown>,
      agentHelper.accessor("paidAt", {
        header: "Data wypłaty",
        cell: (info) => {
          const val = info.getValue();
          return val ? new Date(val).toLocaleDateString("pl-PL") : "—";
        },
      }) as ColumnDef<AgentSettlement, unknown>,
      agentHelper.display({
        id: "actions",
        header: "",
        cell: ({ row }) => {
          const s = row.original;
          return (
            <div className="flex items-center justify-end">
              <button
                type="button"
                onClick={() => {
                  exportExcel.mutate(s.id);
                }}
                disabled={exportExcel.isPending}
                className="rounded-lg p-1.5 text-dim transition-colors hover:bg-muted"
                title="Pobierz rozliczenie"
              >
                <Download size={16} />
              </button>
            </div>
          );
        },
      }) as ColumnDef<AgentSettlement, unknown>,
    ],
    [exportExcel],
  );
}

// ============================
// Main Component
// ============================

export function SettlementsPage() {
  const user = useUser();
  const isAdmin = user?.role === "Administrator";

  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(String(currentYear));
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedAgentId, setSelectedAgentId] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sorting, setSorting] = useState<SortingState>([
    { id: "month", desc: true },
  ]);
  const [approvingSettlementId, setApprovingSettlementId] = useState<string | null>(null);
  const [unlockingSettlementId, setUnlockingSettlementId] = useState<string | null>(null);
  const unlockMutation = useUnlockSettlement();

  // Reset page when filters change
  function handleFilterChange<T>(setter: (v: T) => void) {
    return (v: T) => {
      setter(v);
      setPage(1);
    };
  }

  // Shared filter set: kafelki muszą widzieć dokładnie to samo co tabela (minus paginacja).
  const adminFilters = {
    year: Number(selectedYear),
    month: selectedMonth !== "all" ? Number(selectedMonth) : undefined,
    status: selectedStatus !== "all" ? selectedStatus : undefined,
    agentId: selectedAgentId || undefined,
  };

  const agentFilters = {
    year: Number(selectedYear),
    month: selectedMonth !== "all" ? Number(selectedMonth) : undefined,
    status: selectedStatus !== "all" ? selectedStatus : undefined,
  };

  // Admin: uses GET /settlements (all), Agent: uses GET /settlements/agent/:id
  // Only fire the query matching the user's role to avoid 403 on the other endpoint
  const adminQuery = useAllSettlements(
    { ...adminFilters, page, pageSize },
    isAdmin,
  );

  const agentQuery = useAgentSettlements(
    user?.agentId ?? "",
    Number(selectedYear),
    !isAdmin,
    page,
    pageSize,
    agentFilters.month,
    agentFilters.status,
  );

  // Kafelki (statystyki) agreguje backend z uwzględnieniem WSZYSTKICH stron,
  // a nie tylko bieżącej — inaczej administrator podejmowałby decyzje na
  // podstawie dowolnego fragmentu wyniku. Oddzielne endpointy zapewniają,
  // że agent nigdy nie widzi pól adminowych (totalCommission, uniqueAgents,
  // companyShare) i nie może odczytać cudzych rozliczeń.
  const adminAggregate = useSettlementsAggregate(adminFilters, isAdmin);
  const agentAggregate = useAgentSettlementsAggregate(
    user?.agentId ?? "",
    agentFilters,
    !isAdmin,
  );

  const queryResult = isAdmin ? adminQuery : agentQuery;
  const { isLoading, isFetching } = queryResult;
  const isAggregateFetching = isAdmin
    ? adminAggregate.isFetching
    : agentAggregate.isFetching;

  const adminColumns = useAdminColumns(
    setApprovingSettlementId,
    setUnlockingSettlementId,
    isAdmin,
  );
  const agentCols = useAgentColumns();

  // Admin: server-side filters. Agent: client-side filters.
  const filteredAdminItems = useMemo(
    () => (isAdmin ? ((adminQuery.data?.items ?? []) as Settlement[]) : []),
    [adminQuery.data?.items, isAdmin],
  );

  const filteredAgentItems = useMemo(
    () => (isAdmin ? [] : ((agentQuery.data?.items ?? []) as AgentSettlement[])),
    [agentQuery.data?.items, isAdmin],
  );

  // Stats come straight from the aggregate endpoint — never from the current page.
  const stats = useMemo(() => {
    if (isAdmin) {
      const a = adminAggregate.data;
      return {
        totalCommission: a?.totalCommission ?? 0,
        totalAgentShare: a?.totalAgentShare ?? 0,
        totalPolicies: a?.totalPolicies ?? 0,
        uniqueAgents: a?.uniqueAgents ?? 0,
        count: a?.count ?? 0,
      };
    }
    const a = agentAggregate.data;
    return {
      totalCommission: 0,
      totalAgentShare: a?.totalAgentShare ?? 0,
      totalPolicies: a?.totalPolicies ?? 0,
      uniqueAgents: 0,
      count: a?.count ?? 0,
    };
  }, [isAdmin, adminAggregate.data, agentAggregate.data]);

  // itemCount = rows visible in the table on the current page (for the empty-state check).
  const itemCount = isAdmin
    ? filteredAdminItems.length
    : filteredAgentItems.length;

  const paginationData = isAdmin ? adminQuery.data : agentQuery.data;
  const totalCount = paginationData?.totalCount ?? 0;
  const totalPages = paginationData?.totalPages ?? 1;

  if (approvingSettlementId) {
    return (
      <SettlementApprovalView
        settlementId={approvingSettlementId}
        onClose={() => setApprovingSettlementId(null)}
      />
    );
  }

  if (!user?.agentId && !isAdmin) {
    return (
      <div className="animate-fade-in">
        <PageHeader
          title="Rozliczenia"
          description="Przegląd rozliczeń prowizji"
        />
        <Card>
          <EmptyState
            icon={<Receipt size={32} />}
            title="Brak danych"
            description="Nie znaleziono powiązanego konta agenta."
          />
        </Card>
      </div>
    );
  }

  const periodLabel =
    selectedMonth !== "all"
      ? `${MONTHS[Number(selectedMonth) - 1]} ${selectedYear}`
      : `rok ${selectedYear}`;

  return (
    <div className="animate-fade-in space-y-4">
      <PageHeader
        title="Rozliczenia"
        description={
          isAdmin ? "Przegląd rozliczeń prowizji" : "Przegląd Twoich rozliczeń"
        }
      />

      {/* Filters */}
      <Card padding="sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          {isAdmin && (
            <AgentSelect
              value={selectedAgentId}
              onChange={handleFilterChange(setSelectedAgentId)}
              placeholder="Wszyscy agenci"
              className="w-64"
            />
          )}
          <Select
            value={selectedYear}
            onChange={handleFilterChange(setSelectedYear)}
            options={yearOptions()}
            className="w-32"
            size="sm"
          />
          <Select
            value={selectedMonth}
            onChange={handleFilterChange(setSelectedMonth)}
            options={MONTH_OPTIONS}
            className="w-44"
            size="sm"
          />
          <Select
            value={selectedStatus}
            onChange={handleFilterChange(setSelectedStatus)}
            options={isAdmin ? STATUS_OPTIONS : AGENT_STATUS_OPTIONS}
            className="w-44"
            size="sm"
          />
          <div className="ml-auto flex items-center gap-2">
            {(isFetching || isAggregateFetching) && !isLoading && (
              <Spinner size="sm" />
            )}
          </div>
        </div>
      </Card>

      {/* Summary Stats — liczone przez backend na podstawie WSZYSTKICH stron */}
      {!isLoading && stats.count > 0 && (
        <div
          className={clsx(
            "grid gap-3",
            isAdmin
              ? "grid-cols-2 lg:grid-cols-4"
              : "grid-cols-2 lg:grid-cols-3",
          )}
        >
          <StatCard
            icon={<Receipt size={18} />}
            label={`Rozliczenia — ${periodLabel}`}
            value={String(stats.count)}
          />
          {isAdmin && (
            <StatCard
              icon={<Banknote size={18} />}
              label="Prowizja łączna"
              value={formatCurrency(stats.totalCommission)}
            />
          )}
          <StatCard
            icon={<TrendingUp size={18} />}
            label={isAdmin ? "Wypłaty agentów" : "Łączna kwota"}
            value={formatCurrency(stats.totalAgentShare)}
          />
          {isAdmin ? (
            <StatCard
              icon={<Users size={18} />}
              label="Agenci"
              value={String(stats.uniqueAgents)}
            />
          ) : (
            <StatCard
              icon={<FileSpreadsheet size={18} />}
              label="Polisy"
              value={String(stats.totalPolicies)}
            />
          )}
        </div>
      )}

      {/* Data Table */}
      <Card padding="none">
        {isLoading ? (
          <div className="flex justify-center py-24">
            <Spinner size="lg" />
          </div>
        ) : itemCount > 0 ? (
          isAdmin ? (
            <DataTable<Settlement>
              data={filteredAdminItems}
              columns={adminColumns}
              sorting={sorting}
              onSortingChange={setSorting}
            />
          ) : (
            <DataTable<AgentSettlement>
              data={filteredAgentItems}
              columns={agentCols}
              sorting={sorting}
              onSortingChange={setSorting}
            />
          )
        ) : (
          <EmptyState
            icon={<FileSpreadsheet size={32} />}
            title="Brak rozliczeń"
            description={
              selectedMonth !== "all" ||
              selectedStatus !== "all" ||
              selectedAgentId
                ? "Brak rozliczeń pasujących do wybranych filtrów."
                : `Nie znaleziono rozliczeń za rok ${selectedYear}.`
            }
          />
        )}
        {totalCount > 0 && (
          <UsersPagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            totalPages={totalPages}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        )}
      </Card>

      <UnlockSettlementModal
        isOpen={!!unlockingSettlementId}
        onClose={() => {
          // Nie zamykamy w trakcie lecącego requestu — inaczej można wysłać drugi unlock,
          // który wpadnie w unikat (settlement_id, revision_number) i skończy się 500.
          if (unlockMutation.isPending) {
            return;
          }
          unlockMutation.reset();
          setUnlockingSettlementId(null);
        }}
        isLoading={unlockMutation.isPending}
        submitError={
          unlockMutation.isError
            ? ((unlockMutation.error as Error)?.message ??
              "Nie udało się cofnąć rozliczenia.")
            : null
        }
        onSubmit={(reason) => {
          if (!unlockingSettlementId) return;
          unlockMutation.mutate(
            { id: unlockingSettlementId, reason },
            {
              onSuccess: () => {
                unlockMutation.reset();
                setUnlockingSettlementId(null);
                toast.success(
                  "Rozliczenie cofnięte do edycji — ma teraz status „Obliczone”.",
                );
              },
            },
          );
        }}
      />
    </div>
  );
}
